/**
 * Utility functions for handling AB phenotype splitting and joining
 */

export interface SplitPhenotype {
  aPhenotype: string;
  bPhenotype: string;
}

/**
 * Splits an AB phenotype string into A and B parts
 * Example input: "A1 A1 A*01:01 A*01:01 B17 B57 B*57:01 B37 B*37:01 Bw4 Cw6 Cw6 C*06:02 C*06:02"
 * Example output: { aPhenotype: "A1 A1 A*01:01 A*01:01", bPhenotype: "B17 B57 B*57:01 B37 B*37:01 Bw4 Cw6 Cw6 C*06:02 C*06:02" }
 */
export function splitAbPhenotype(abPhenotype: string | undefined): SplitPhenotype {
  if (!abPhenotype || abPhenotype.trim() === '') {
    return { aPhenotype: '', bPhenotype: '' };
  }

  const parts = abPhenotype.trim().split(/\s+/);
  const aParts: string[] = [];
  const bParts: string[] = [];

  let foundB = false;

  for (const part of parts) {
    // Check if we've reached B markers (B, Bw, or C markers usually follow B antigens)
    if (part.startsWith('B') && !part.startsWith('Bw') && !foundB) {
      foundB = true;
      bParts.push(part);
    } else if (foundB) {
      bParts.push(part);
    } else {
      // A antigens and their related markers come first
      aParts.push(part);
    }
  }

  return {
    aPhenotype: aParts.join(' '),
    bPhenotype: bParts.join(' ')
  };
}

/**
 * Joins A and B phenotype parts back into a single AB phenotype string
 * Example input: { aPhenotype: "A1 A1 A*01:01 A*01:01", bPhenotype: "B17 B57 B*57:01 B37 B*37:01 Bw4 Cw6 Cw6 C*06:02 C*06:02" }
 * Example output: "A1 A1 A*01:01 A*01:01 B17 B57 B*57:01 B37 B*37:01 Bw4 Cw6 Cw6 C*06:02 C*06:02"
 */
export function joinAbPhenotype(aPhenotype: string | undefined, bPhenotype: string | undefined): string {
  const aPart = (aPhenotype || '').trim();
  const bPart = (bPhenotype || '').trim();

  if (!aPart && !bPart) {
    return '';
  }

  if (!aPart) {
    return bPart;
  }

  if (!bPart) {
    return aPart;
  }

  return `${aPart} ${bPart}`;
}

/**
 * Validates that a phenotype part contains valid characters
 */
export function validatePhenotypepart(phenotypePart: string): boolean {
  if (!phenotypePart) return true; // Empty is valid
  // Allow letters, numbers, asterisks, colons, hyphens, and spaces
  return /^[A-Za-z0-9*:\- ]*$/.test(phenotypePart);
}